import React from 'react';
import { Box, Typography } from '@mui/material';
import { BookOpen } from 'lucide-react';

const navy = '#1e3a5f';
const goldDark = '#9a7209';
const cream = '#faf8f4';
const font = 'Frank Ruhl Libre, David, serif';

const CATEGORY_ACCENT = {
  gemara:  '#1e3a5f',
  halacha: '#9a7209',
  tanakh:  '#2d5016',
  mishna:  '#7c3aed',
  mussar:  '#b45309',
};

const CategoryTile = ({ label, accent, items }) => (
  <Box sx={{
    border: '1px solid #e8e0d0', borderRadius: '8px', bgcolor: 'white',
    borderTop: `3px solid ${accent}`,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    mb: 1.25, overflow: 'hidden',
  }}>
    {/* Category header */}
    <Box sx={{ px: 1.5, py: 0.6, borderBottom: '1px solid #f0ece4' }}>
      <Typography sx={{ fontFamily: font, fontSize: '0.62rem', fontWeight: 700, color: accent, letterSpacing: '0.05em' }}>
        {label}
      </Typography>
    </Box>

    {/* Items */}
    {items.map((item, i) => (
      <Box key={item.key || i} sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        px: 1.5, py: 0.6,
        borderBottom: i < items.length - 1 ? '1px solid #f0ece4' : 'none',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography sx={{ fontFamily: font, fontSize: '0.68rem', color: navy, fontWeight: 700 }}>
            {item.labelHe}
          </Typography>
          {item.url && <BookOpen size={9} style={{ color: goldDark, opacity: 0.5, flexShrink: 0, marginTop: 2 }} />}
        </Box>
        <Typography sx={{ fontFamily: font, fontSize: '0.66rem', color: '#4b5563', fontWeight: 500 }}>
          {item.valueHe}
        </Typography>
      </Box>
    ))}
  </Box>
);

/**
 * Tiles — "אריחים"
 * כרטיסים צבעוניים לפי קטגוריה, מסודר ונקי.
 */
const Tiles = ({ today, items, groups, loading, error, title, showTitle }) => {
  const hebrewDate = today?.hebrewDate?.rendered || '';
  const displayGroups = groups || [];
  const flatItems = !groups && Array.isArray(items) ? items : null;

  return (
    <Box sx={{
      width: '100%', height: '100%', overflow: 'hidden',
      bgcolor: cream, fontFamily: font, direction: 'rtl', p: 2,
    }}>
      <Box sx={{ height: '100%', overflow: 'auto',
        '&::-webkit-scrollbar': { width: '2px' },
        '&::-webkit-scrollbar-thumb': { background: '#d4c9b0', borderRadius: '1px' },
      }}>
        {/* Header */}
        {showTitle && (
          <Typography sx={{
            textAlign: 'center', fontFamily: font, fontSize: '0.82rem', fontWeight: 700,
            color: navy, letterSpacing: '0.08em',
          }}>
            {title}
          </Typography>
        )}

        {hebrewDate && (
          <Typography sx={{ textAlign: 'center', fontFamily: font, fontSize: '0.6rem', color: goldDark, mb: 0.5 }}>
            {hebrewDate}
          </Typography>
        )}

        {/* Divider */}
        <Box sx={{
          height: '1px', my: 1,
          background: `linear-gradient(90deg, transparent, ${navy}, transparent)`,
          opacity: 0.15,
        }} />

        {/* Tiles */}
        {displayGroups.map((group) => (
          <CategoryTile
            key={group.category}
            label={group.label}
            accent={CATEGORY_ACCENT[group.category] || navy}
            items={group.items}
          />
        ))}

        {/* Flat fallback for preview */}
        {flatItems && (
          <CategoryTile label="לימודים יומיים" accent={navy} items={flatItems} />
        )}
      </Box>
    </Box>
  );
};

export default Tiles;
